import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, Form, Row, Col, Button, Badge, Spinner } from 'react-bootstrap';
import apiClient from '../../api';
import { toast } from 'react-toastify';

const BookingHeatmapView = ({ bases = [] }) => {
    const [loading, setLoading] = useState(false);
    const [points, setPoints] = useState([]);
    const [zones, setZones] = useState([]);
    const mapRef = useRef(null);
    const leafletMapInstance = useRef(null);
    const heatLayerInstance = useRef(null);

    // Toggles de Capas en el Mapa
    const [showZones, setShowZones] = useState(true);
    const [showPins, setShowPins] = useState(true);
    const [showHeatmap, setShowHeatmap] = useState(true);
    const [onlyNoAvail, setOnlyNoAvail] = useState(false);

    const [filters, setFilters] = useState({
        start_date: new Date(Date.now() - 7 * 86400000).toISOString().split('T')[0],
        end_date: new Date().toISOString().split('T')[0],
        country: '',
        base_id: ''
    });

    // Cargar Leaflet y el plugin de Mapa de Calor (Leaflet.heat) dinámicamente
    useEffect(() => {
        const loadScripts = async () => {
            if (!window.L) {
                const link = document.createElement('link');
                link.rel = 'stylesheet';
                link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
                document.head.appendChild(link);

                await new Promise(resolve => {
                    const script = document.createElement('script');
                    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
                    script.onload = resolve;
                    document.head.appendChild(script);
                });
            }

            if (window.L && !window.L.heatLayer) {
                await new Promise(resolve => {
                    const heatScript = document.createElement('script');
                    heatScript.src = 'https://unpkg.com/leaflet.heat@0.2.0/dist/leaflet-heat.js';
                    heatScript.onload = resolve;
                    document.head.appendChild(heatScript);
                });
            }

            initMap();
        };

        loadScripts();

        return () => {
            if (leafletMapInstance.current) {
                leafletMapInstance.current.remove();
                leafletMapInstance.current = null;
            }
        };
    }, []);

    const initMap = () => {
        if (!mapRef.current || leafletMapInstance.current || !window.L) return;

        // Iniciar mapa en Lima / América Latina por defecto
        const map = window.L.map(mapRef.current).setView([-12.046374, -77.042793], 11);

        window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            maxZoom: 19,
            attribution: '© OpenStreetMap contributors'
        }).addTo(map);

        leafletMapInstance.current = map;
    };

    const fetchHeatmapData = useCallback(async () => {
        setLoading(true);
        try {
            const params = {
                start_date: filters.start_date,
                end_date: filters.end_date,
                country: filters.country || undefined,
                base_id: filters.base_id || undefined,
                status: onlyNoAvail ? 'NO_AVAILABILITY' : undefined
            };

            const res = await apiClient.get('/booking/analytics/heatmap-clusters', { params });
            const pts = res.data.points || [];
            const zns = res.data.zones || [];

            setPoints(pts);
            setZones(zns);

            renderMapLayers(pts, zns);
        } catch (err) {
            console.error("Error al obtener datos de mapa de calor Booking:", err);
            toast.error("Error al cargar los datos del mapa de calor.");
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, onlyNoAvail]);

    useEffect(() => {
        if (leafletMapInstance.current) {
            fetchHeatmapData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchHeatmapData, showZones, showPins, showHeatmap]);

    const renderMapLayers = (pts, zns) => {
        const map = leafletMapInstance.current;
        if (!map || !window.L) return;

        // Limpiar capas previas de marcadores y polígonos
        map.eachLayer(layer => {
            if (layer instanceof window.L.Polygon || layer instanceof window.L.Marker || layer instanceof window.L.CircleMarker) {
                map.removeLayer(layer);
            }
        });

        if (heatLayerInstance.current) {
            map.removeLayer(heatLayerInstance.current);
            heatLayerInstance.current = null;
        }

        // Sanitizar puntos: Auto-corregir inversión de lat/lng y filtrar dentro del continente
        const validPts = pts.map(p => {
            let lat = parseFloat(p.lat);
            let lng = parseFloat(p.lng);
            if (lat < -50.0 && lng > -50.0) {
                const temp = lat;
                lat = lng;
                lng = temp;
            }
            return { ...p, lat, lng };
        }).filter(p => !isNaN(p.lat) && !isNaN(p.lng) && -56.0 <= p.lat && p.lat <= 30.0 && -115.0 <= p.lng && p.lng <= -30.0);

        const bounds = [];

        // 2. Renderizar Polígonos de Zonas Activas
        if (showZones) {
            zns.forEach(z => {
                if (Array.isArray(z.coords) && z.coords.length > 0) {
                    const latLngs = z.coords.map(c => [c.lat, c.lng]);
                    const polygon = window.L.polygon(latLngs, {
                        color: '#0d6efd',
                        weight: 2,
                        fillColor: '#0d6efd',
                        fillOpacity: 0.12
                    }).addTo(map);

                    polygon.bindPopup(`
                        <div style="font-size:12px;">
                            <strong style="color:#0d6efd;">🔷 ZONA OPERATIVA ACTIVA</strong><br/>
                            <b>Nombre:</b> ${z.name}<br/>
                            <b>Base ID:</b> #${z.base_id || 'N/A'}
                        </div>
                    `);
                    latLngs.forEach(ll => bounds.push(ll));
                }
            });
        }

        // 3. Renderizar Capa de Mapa de Calor (Heatmap Layer)
        if (showHeatmap && window.L.heatLayer && validPts.length > 0) {
            const heatData = validPts
                .map(p => [p.lat, p.lng, Math.min(1.0, (p.weight || 1) / 10)]);

            if (heatData.length > 0) {
                heatLayerInstance.current = window.L.heatLayer(heatData, {
                    radius: 30,
                    blur: 20,
                    maxZoom: 16,
                    gradient: { 0.2: '#0000ff', 0.4: '#00ffff', 0.6: '#00ff00', 0.8: '#ffff00', 1.0: '#ff0000' }
                }).addTo(map);
            }
        }

        // 4. Renderizar PINES VISIBLES CON BARRAS/CONTEOS (Badge Pins)
        if (showPins) {
            validPts.forEach(p => {
                bounds.push([p.lat, p.lng]);

                const bgBg = p.weight > 10 ? '#dc3545' : (p.weight > 3 ? '#fd7e14' : '#198754');
                const pinHtml = `
                    <div style="
                        background: ${bgBg};
                        color: white;
                        font-weight: 800;
                        font-size: 11px;
                        padding: 3px 8px;
                        border-radius: 12px;
                        border: 2px solid white;
                        box-shadow: 0 3px 8px rgba(0,0,0,0.4);
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        white-space: nowrap;
                        cursor: pointer;
                    ">
                        📍 ${p.weight}
                    </div>
                `;

                const customIcon = window.L.divIcon({
                    className: 'demand-pin-badge',
                    html: pinHtml,
                    iconSize: [46, 26],
                    iconAnchor: [23, 13]
                });

                const marker = window.L.marker([p.lat, p.lng], { icon: customIcon }).addTo(map);

                marker.bindPopup(`
                    <div style="font-size:12px; min-width: 180px;">
                        <strong style="color: ${bgBg}; font-size:13px;">📍 Punto de Demanda Booking</strong><br/>
                        <div className="mt-1"><b>Dirección:</b> ${p.label || 'Coordenada'}</div>
                        <div><b>Total Consultas:</b> <span style="color:${bgBg}; font-weight:bold; font-size:14px;">${p.weight}</span></div>
                        <div className="text-muted small">Lat/Lng: ${p.lat.toFixed(4)}, ${p.lng.toFixed(4)}</div>
                    </div>
                `);
            });
        }

        // Ajustar encuadre automático del mapa (Enfocar inteligentemente según país)
        if (bounds.length > 0) {
            try {
                const effectiveBounds = filters.country === 'argentina' 
                    ? bounds 
                    : bounds.filter(b => b[0] > -50.0);
                
                if (effectiveBounds.length > 0) {
                    map.fitBounds(effectiveBounds, { padding: [40, 40] });
                } else {
                    map.fitBounds(bounds, { padding: [40, 40] });
                }
            } catch (e) { }
        }
    };

    const handleFilterChange = (key, val) => {
        setFilters(prev => ({ ...prev, [key]: val }));
    };

    return (
        <div className="animate-fade-in">
            {/* FILTROS Y CONTROLES DE CAPAS */}
            <Card className="mb-3 shadow-sm border-0">
                <Card.Header className="bg-dark text-white fw-bold d-flex justify-content-between align-items-center py-2">
                    <span>🗺️ Análisis de Polígonos de Cobertura y Puntos de Alta Demanda (OpenStreetMap)</span>
                    <Badge bg="primary">Gratis y Sin Límite</Badge>
                </Card.Header>
                <Card.Body className="p-3 bg-light">
                    <Row className="g-2 align-items-end mb-2">
                        <Col xl={2} lg={3} md={4} sm={6}>
                            <Form.Label className="small fw-bold text-muted mb-1">Fecha Inicio</Form.Label>
                            <Form.Control 
                                type="date" 
                                size="sm" 
                                value={filters.start_date} 
                                onChange={e => handleFilterChange('start_date', e.target.value)} 
                            />
                        </Col>
                        <Col xl={2} lg={3} md={4} sm={6}>
                            <Form.Label className="small fw-bold text-muted mb-1">Fecha Fin</Form.Label>
                            <Form.Control 
                                type="date" 
                                size="sm" 
                                value={filters.end_date} 
                                onChange={e => handleFilterChange('end_date', e.target.value)} 
                            />
                        </Col>
                        <Col xl={2} lg={3} md={4} sm={6}>
                            <Form.Label className="small fw-bold text-muted mb-1">País / Región</Form.Label>
                            <Form.Select 
                                size="sm" 
                                value={filters.country} 
                                onChange={e => handleFilterChange('country', e.target.value)}
                            >
                                <option value="">(Todos los Países)</option>
                                <option value="chile">Chile (SCL / Regiones)</option>
                                <option value="peru">Perú (Lima / Arequipa)</option>
                                <option value="dominicana">Rep. Dominicana (Punta Cana)</option>
                                <option value="argentina">Argentina (EZE / Mendoza)</option>
                                <option value="brasil">Brasil (São Paulo / Río)</option>
                            </Form.Select>
                        </Col>
                        <Col xl={2} lg={3} md={4} sm={6}>
                            <Form.Label className="small fw-bold text-muted mb-1">Base / Sub Sede</Form.Label>
                            <Form.Select 
                                size="sm" 
                                value={filters.base_id} 
                                onChange={e => handleFilterChange('base_id', e.target.value)}
                            >
                                <option value="">(Todas las Bases)</option>
                                {bases.map(b => (
                                    <option key={b.id} value={b.id}>{b.name}</option>
                                ))}
                            </Form.Select>
                        </Col>
                        <Col xl={4} lg={12} md={8} sm={12}>
                            <Button variant="primary" size="sm" className="w-100 fw-bold" onClick={fetchHeatmapData} disabled={loading}>
                                {loading ? <Spinner animation="border" size="sm" /> : "Actualizar Capa de Mapa"}
                            </Button>
                        </Col>
                    </Row>

                    {/* SELECTORES DE CAPAS Y DE SIN COBERTURA */}
                    <hr className="my-2" />
                    <Row className="align-items-center g-2">
                        <Col md={3} sm={6}>
                            <Form.Check 
                                type="switch"
                                id="switch-pins"
                                label={<span className="fw-bold small">📍 Mostrar Pines con Conteo ({points.length})</span>}
                                checked={showPins}
                                onChange={e => setShowPins(e.target.checked)}
                            />
                        </Col>
                        <Col md={3} sm={6}>
                            <Form.Check 
                                type="switch"
                                id="switch-heatmap"
                                label={<span className="fw-bold small">🔴 Mostrar Mapa de Calor (Heatmap)</span>}
                                checked={showHeatmap}
                                onChange={e => setShowHeatmap(e.target.checked)}
                            />
                        </Col>
                        <Col md={3} sm={6}>
                            <Form.Check 
                                type="switch"
                                id="switch-zones"
                                label={<span className="fw-bold small">🔷 Mostrar Polígonos ({zones.length})</span>}
                                checked={showZones}
                                onChange={e => setShowZones(e.target.checked)}
                            />
                        </Col>
                        <Col md={3} sm={6}>
                            <Form.Check 
                                type="switch"
                                id="switch-no-avail"
                                label={<span className="fw-bold small text-danger">⚠️ Solo Sin Cobertura (NO_AVAILABILITY)</span>}
                                checked={onlyNoAvail}
                                onChange={e => setOnlyNoAvail(e.target.checked)}
                            />
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* MAPA INTERACTIVO Y LEYENDA */}
            <Card className="shadow-sm border-0 mb-4">
                <Card.Header className="bg-white py-2 d-flex justify-content-between align-items-center">
                    <span className="small text-muted fw-bold">
                        🔷 Polígonos Operativos ({zones.length}) | 📍 Pines de Consultas ({points.length})
                    </span>
                    <div className="d-flex gap-2 align-items-center">
                        <span className="badge bg-danger">Alta Demanda (>10)</span>
                        <span className="badge bg-warning text-dark">Media (>3)</span>
                        <span className="badge bg-success">Normal</span>
                    </div>
                </Card.Header>
                <Card.Body className="p-0">
                    <div 
                        ref={mapRef} 
                        style={{ width: '100%', height: '620px', borderRadius: '0 0 8px 8px', zIndex: 1 }} 
                    />
                </Card.Body>
            </Card>
        </div>
    );
};

export default BookingHeatmapView;
